import React from 'react';
import { useTranslation } from 'react-i18next';
import { ReportStep } from '../../hooks/report/types';
import { ReportItemData } from '../../types/reportProgress';
import { getStatusText, formatProcessingTime } from '../../utils/reportProcessingUtils';

interface ReportCardsListProps {
  reports: ReportStep[];
  progressItems?: ReportItemData[];
  onCardClick?: (reportIndex: number) => void;
}

/**
 * ReportCardsList component - displays a list of report cards
 */
const ReportCardsList: React.FC<ReportCardsListProps> = ({ reports, progressItems, onCardClick }) => {
  const { t } = useTranslation('create');
  // Filter out deleted reports and sort by index
  const filteredReports = reports.filter(report => !report.deleted);
  const sortedReports = [...filteredReports].sort((a, b) => a.index - b.index);
  
  return (
    <div className="report-cards-list">
      <h3>{t('results.reports_title')}</h3>

      {sortedReports.length === 0 ? (
        <div className="no-reports">
          <p>{t('results.no_reports_list')}</p>
        </div>
      ) : (
        <ul className="reports-list">
          {sortedReports.map((report) => (
            <li
              key={report.index}
              className={`report-card-item ${report.status}`}
              onClick={() => onCardClick?.(report.index)}
              style={{ cursor: onCardClick ? 'pointer' : 'default' }}
            >
              <div className="report-card-header">
                <span className="report-name">{report.name}</span>
                <div className="report-status-info">
                  <span className={`status-badge ${report.status}`}>
                    {getStatusText(report.status)}
                  </span>
                  {(report.processing_duration_seconds || progressItems?.find(i => i.index === report.index)?.completed_at) && (
                    <span className="processing-time">
                      {report.processing_duration_seconds
                        ? formatProcessingTime(report.processing_duration_seconds)
                        : (() => {
                            const item = progressItems?.find(i => i.index === report.index);
                            if (item?.started_at && item?.completed_at) {
                              const sec = (new Date(item.completed_at).getTime() - new Date(item.started_at).getTime()) / 1000;
                              return formatProcessingTime(Math.round(sec));
                            }
                            return null;
                          })()
                      }
                    </span>
                  )}
                </div>
              </div>
              
              {report.sheets && (
                <div className="report-sheets">
                  <h5>{t('analytics_step.sheets_title')}</h5>
                  <div className="compact-list">
                    {report.sheets.map((sheet: string, sheetIdx: number) => (
                      <span key={sheetIdx} className="compact-item">{sheet}</span>
                    ))}
                  </div>
                </div>
              )}
              
              {report.processing_steps && (
                <div className="report-processing-steps">
                  <h5>{t('analytics_step.steps_title')}</h5>
                  <div className="compact-list">
                    {report.processing_steps.map((step: string, stepIdx: number) => (
                      <div key={stepIdx} className="compact-item">{stepIdx + 1}. {step}</div>
                    ))}
                  </div>
                </div>
              )}
              
              {report.error && (
                <div className="report-error">
                  <p>{report.error}</p>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default ReportCardsList;
